import {
  Combobox,
  type ComboboxProps,
  type OptionOnSelectData,
  type SelectionEvents,
} from "@fluentui/react-components";
import { useControllableValue } from "@fluentui/react-hooks";
import * as React from "react";
import { useStyles } from "../PromptFilter.styles";
import { PromptFiltersSkeleton } from "../PromptFilterSkeleton";
import { usePromptFilterLocalizations } from "../usePromptFilterStrings";
import { groupFilters } from "../utils";
import type { FilterProps } from "./Filter.types";
import { RenderOptions } from "./RenderOptions";

/**
 * Combobox filter to select a single item using text input.
 * This component does not yet support multi-select.
 * @param param0 props
 * @returns ComboboxFilter
 */
export const ComboboxFilter: React.FC<FilterProps> = ({
  appearance,
  tab,
  filter,
  isLoading,
  selectedValues,
  defaultSelectedValues,
  onChange,
  onOpenChanged,
}) => {
  const styles = useStyles(appearance)();
  const [values, setValues] = useControllableValue(
    selectedValues,
    defaultSelectedValues,
    onChange
  );
  const {
    facetKeyToLabel,
    facetValueToLabel,
    groupValueToLabel,
    convertValuesToString,
  } = usePromptFilterLocalizations();
  const filterLabel = facetKeyToLabel(filter.key);
  const filterByGroup = groupFilters(filter);
  const defaultDisplayedValueText = convertValuesToString(
    filter,
    defaultSelectedValues
  );
  const [displayedValueText, setDisplayedValueText] = React.useState<
    string | undefined
  >(undefined);

  const onOptionSelect = (
    _: SelectionEvents,
    data: OptionOnSelectData
  ): void => {
    setValues(data.selectedOptions);

    if (filter.isSingleSelect) {
      setDisplayedValueText(
        convertValuesToString(filter, data.selectedOptions)
      );
    } else {
      // if multiselect, we should clear the text input, so that the user can start typing another option again
      setDisplayedValueText("");
    }
  };

  const handleOnOpenChanged: ComboboxProps["onOpenChange"] = React.useCallback(
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (_e: any, data: any) => {
      // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
      onOpenChanged && onOpenChanged(data.open, [filter]);
    },
    [filter, onOpenChanged]
  );

  const onFocus = () => {
    setDisplayedValueText("");
  };

  const onBlur = () => {
    setDisplayedValueText(convertValuesToString(filter, values));
  };

  const onInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setDisplayedValueText(event.target.value);
  };

  if (filter.options.length < 1) {
    return null;
  }

  return (
    <React.Fragment key={`${tab}-${filter.key}`}>
      {isLoading ? (
        <PromptFiltersSkeleton />
      ) : (
        <Combobox
          data-testid={`${filter.key}-combobox`}
          aria-label={filterLabel}
          className={styles.dropdown}
          multiselect={!filter.isSingleSelect}
          placeholder={filterLabel}
          defaultValue={defaultDisplayedValueText}
          value={displayedValueText ?? defaultDisplayedValueText}
          defaultSelectedOptions={values ?? []}
          onOptionSelect={onOptionSelect}
          onOpenChange={handleOnOpenChanged}
          onChange={onInputChange}
          onFocus={onFocus}
          onBlur={onBlur}
          inlinePopup={true}
          style={{ zIndex: 2 }} // Added z-index style so box doesn't get hidden behind other elements
          listbox={
            filter.isOverflowItemsFrom10 && {
              className: styles.filterListBoxOverflowFrom10Items,
            }
          }
          positioning={
            filter.width === "wide"
              ? {
                  autoSize: "width",
                }
              : {}
          }
        >
          {Object.entries(filterByGroup).map(([groupKey, filterOptions]) => (
            <React.Fragment key={groupKey}>
              <RenderOptions
                groupKey={groupKey}
                groupLabel={groupValueToLabel(groupKey)}
                filterOptions={filterOptions}
                selectedValues={values ?? []}
                valueToLabel={facetValueToLabel}
                appearance={appearance}
              />
            </React.Fragment>
          ))}
        </Combobox>
      )}
    </React.Fragment>
  );
};
